import { Button } from './ui/button';
import { useCartStore } from '../stores/cartStore';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Minus,
  Plus,
  Trash2,
  ShoppingBag,
  ArrowRight,
  Truck,
} from 'lucide-react';
import type { Product } from './ProductCard';
import { useTranslation } from 'react-i18next';

interface CartItem extends Product {
  quantity: number;
  variant?: any;
  organic?: boolean;
}

interface SideCartProps {
  onClose: () => void;
  onUpdateQuantity: (id: string, quantity: number, variant?: any) => void;
  onRemoveItem: (id: string, variant?: any) => void;
  onCartPageClick: () => void;
  onCheckout: () => void;
}

export function SideCart({
  onClose,
  onUpdateQuantity,
  onRemoveItem,
  onCartPageClick,
  onCheckout,
}: SideCartProps) {
  const { t } = useTranslation('cart');
  // 🎯 Прямое подключение к CartStore - SideCart полностью самоуправляемый!
  const cartItems = useCartStore(state => state.cartItems);
  const isOpen = useCartStore(state => state.isCartOpen);
  const setCartOpen = useCartStore(state => state.setCartOpen);
  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getSubtotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
  };

  const getShippingThreshold = () => {
    const subtotal = getSubtotal();
    const freeShippingThreshold = 40;
    return Math.max(0, freeShippingThreshold - subtotal);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          setCartOpen(false);
          onClose?.();
        }
      }}
      modal={false}
    >
      <SheetContent
        side="right"
        className="w-full sm:w-96 p-0 border-0"
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(25px)',
          WebkitBackdropFilter: 'blur(25px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow:
            '0 16px 64px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(245, 158, 11, 0.1)',
        }}
      >
        {/* Neural gradient overlay */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background:
              'linear-gradient(135deg, rgba(245, 158, 11, 0.03) 0%, rgba(217, 119, 6, 0.01) 50%, rgba(180, 83, 9, 0.03) 100%)',
          }}
        />

        <div className="relative flex flex-col h-full">
          <SheetHeader
            className="p-6 relative"
            style={{
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="p-2 rounded-2xl"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.15) 100%)',
                  }}
                >
                  <ShoppingBag className="h-6 w-6 text-amber-700" />
                </div>
                <div>
                  <SheetTitle className="text-gray-900 text-xl">
                    {t('title')}
                  </SheetTitle>
                  <SheetDescription className="text-gray-600">
                    {getTotalItems()}{' '}
                    {t(
                      getTotalItems() === 1
                        ? 'itemCount.single'
                        : 'itemCount.plural'
                    )}
                  </SheetDescription>
                </div>
              </div>
            </div>
          </SheetHeader>

          {cartItems.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center">
                <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('empty.title')}
                </h3>
                <p className="text-gray-600 mb-6">{t('empty.subtitle')}</p>
                <Button
                  onClick={onClose}
                  className="bg-amber-900 hover:bg-amber-800 text-white"
                >
                  {t('empty.button')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Free Shipping Progress */}
              {getShippingThreshold() > 0 ? (
                <div
                  className="p-4 relative"
                  style={{
                    background: 'rgba(34, 197, 94, 0.05)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="h-6 w-6 text-green-600" />
                    <span className="text-sm text-green-700 font-medium">
                      {t('shipping.freeThreshold', {
                        amount: formatPrice(getShippingThreshold()),
                      })}
                    </span>
                  </div>
                  <div className="w-full bg-green-100 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(100, (getSubtotal() / 40) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div
                  className="p-4 relative"
                  style={{
                    background: 'rgba(34, 197, 94, 0.05)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <div className="flex items-center gap-2 text-green-700">
                    <Truck className="h-6 w-6" />
                    <span className="text-sm font-medium">
                      {t('shipping.qualified')}
                    </span>
                  </div>
                </div>
              )}

              {/* Cart Items and Summary in one scrollable area */}
              <ScrollArea className="flex-1 overflow-y-auto">
                <div className="p-4 space-y-4">
                  {cartItems.map(item => (
                    <div
                      key={item.id}
                      className="p-4 rounded-2xl transition-all duration-300"
                      style={{
                        background: 'rgba(255, 255, 255, 0.6)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <div className="flex gap-4">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-xl"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 truncate">
                            {item.name}
                          </h4>
                          {item.variant && (
                            <Badge className="text-xs bg-amber-100 text-amber-800 mt-1">
                              {item.variant.weight} {item.variant.unit || 'g'}
                            </Badge>
                          )}

                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-lg"
                                onClick={() =>
                                  onUpdateQuantity(
                                    item.id,
                                    Math.max(0, item.quantity - 1),
                                    item.variant
                                  )
                                }
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="font-medium text-gray-900 min-w-[2rem] text-center">
                                {item.quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-lg"
                                onClick={() =>
                                  onUpdateQuantity(
                                    item.id,
                                    item.quantity + 1,
                                    item.variant
                                  )
                                }
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>

                            <div className="flex items-center gap-3">
                              <span className="font-semibold text-gray-900">
                                {formatPrice(item.price * item.quantity)}
                              </span>
                              <Button
                                variant="ghost"
                                className="h-10 w-10 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() =>
                                  onRemoveItem(item.id, item.variant)
                                }
                              >
                                <Trash2 className="h-6 w-6" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Cart Summary - now inside ScrollArea */}
                <div
                  className="p-6 mx-4 mb-4 rounded-2xl"
                  style={{
                    background: 'rgba(249, 250, 251, 0.95)',
                    backdropFilter: 'blur(15px)',
                    WebkitBackdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">
                        {t('summary.subtotal')}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {formatPrice(getSubtotal())}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">
                        {t('summary.shipping')}
                      </span>
                      <span className="font-semibold text-gray-900">
                        {getSubtotal() >= 40
                          ? t('shipping.free')
                          : t('shipping.cost')}
                      </span>
                    </div>

                    <Separator className="bg-gray-200" />

                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">
                        {t('summary.total')}
                      </span>
                      <span className="font-bold text-xl text-gray-900">
                        {formatPrice(
                          getSubtotal() + (getSubtotal() >= 40 ? 0 : 4.50)
                        )}
                      </span>
                    </div>

                    <div className="space-y-3 pt-4">
                      <Button
                        className="w-full h-12 bg-amber-900 hover:bg-amber-800 text-white rounded-2xl transition-all duration-300 hover:scale-105"
                        onClick={() => {
                          onCheckout();
                          onClose();
                        }}
                      >
                        {t('actions.checkout')}
                      </Button>

                      <Button
                        variant="outline"
                        className="w-full h-12 rounded-2xl transition-all duration-300 hover:bg-amber-50 border-amber-200"
                        onClick={() => {
                          onCartPageClick();
                          onClose();
                        }}
                      >
                        <span>{t('actions.viewFullCart')}</span>
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
